import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { routesApi, userApi } from '../services/api';
import { Map } from '../components/Map';

function buildGoogleMapsRouteUrl(
  home: { lat: number; lng: number } | null,
  stops: Array<{ lat: number; lng: number }>
): string {
  const base = 'https://www.google.com/maps/dir/?api=1';
  const origin = home ? `${home.lat},${home.lng}` : `${stops[0].lat},${stops[0].lng}`;
  const destination = home ? `${home.lat},${home.lng}` : `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
  const waypoints = stops.map((s) => `${s.lat},${s.lng}`).join('|');
  return `${base}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(waypoints)}&travelmode=driving`;
}

export function DailyRoute() {
  const { date } = useParams<{ date: string }>();

  const { data: route, isLoading, error } = useQuery({
    queryKey: ['route', date],
    queryFn: () => routesApi.getDay(date!),
    enabled: !!date,
  });

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => userApi.get(),
  });

  if (!date) {
    return <div>Invalid date</div>;
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading route...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to load route. Please try again.</p>
        <Link to="/schedule" className="text-blue-600 hover:underline">
          Back to Schedule
        </Link>
      </div>
    );
  }

  if (!route || route.stops.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No visits scheduled for {date}</p>
        <Link to="/schedule" className="text-blue-600 hover:underline">
          Back to Schedule
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/schedule" className="text-blue-600 hover:underline text-sm">
            &larr; Back to Schedule
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 mt-2">Route for {date}</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Drive Time</p>
            <p className="text-lg font-medium">{route.total_drive_time_minutes} min</p>
          </div>
          {route.stops.length > 0 && (() => {
            const stops = route.stops.map((s) => ({ lat: s.store.lat, lng: s.store.lng }));
            const home = user?.home_lat && user?.home_lng ? { lat: user.home_lat, lng: user.home_lng } : null;
            const url = buildGoogleMapsRouteUrl(home, stops);
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Open Route in Google Maps
              </a>
            );
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Route List */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {route.stops.map((stop) => (
              <div key={stop.visit.id} className="p-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {stop.order}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{stop.store.name}</p>
                    {stop.store.chain_name && (
                      <p className="text-sm text-gray-500">{stop.store.chain_name}</p>
                    )}
                    <div className="mt-2 text-sm text-gray-600">
                      <p>
                        Arrive: <span className="font-medium">{stop.arrival_time}</span>
                      </p>
                      <p>
                        Depart: <span className="font-medium">{stop.departure_time}</span>
                      </p>
                      <p>
                        Duration: {stop.visit.duration_minutes} min
                      </p>
                    </div>
                    {stop.drive_time_from_previous > 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        {stop.drive_time_from_previous} min drive from previous
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden h-[600px]">
          <Map
            markers={route.stops.map((stop) => ({
              id: stop.store.id,
              lat: stop.store.lat,
              lng: stop.store.lng,
              name: `${stop.order}. ${stop.store.name}`,
              label: String(stop.order),
            }))}
            showRoute
            routePoints={route.stops.map((stop) => ({
              lat: stop.store.lat,
              lng: stop.store.lng,
            }))}
            showBoundary
          />
        </div>
      </div>
    </div>
  );
}
